import { AxiosResponse } from 'axios';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import qs from 'query-string';
import axios from 'libs/Axios';
import { AxiosDefaultErrorEntity } from 'types/api/Common/ErrorTypes';
import {
  PlatformMigrationRun,
  PlatformMigrationRunItem,
  PlatformMigrationRunRequest,
  PlatformMigrationRunResponse,
  HealthCheckRun,
  HealthCheckItem,
  HealthCheckRunRequest,
  SyncRun,
  SyncItem,
  SyncRunRequest,
  SyncRunResponse,
} from 'types/api/TenantMaster/PlatformMigrationTypes';
import { TenantListResponse } from 'types/api/TenantMaster/MasterTable/TenantTableDataTypes';
import { SystemListResponse } from 'types/api/TenantMaster/MasterTable/SystemTableDataTypes';

// Upper bound for the tenant/system pickers in the migration trigger dialog.
const SCOPE_OPTION_PAGE_SIZE = 1000;

const POLL_INTERVAL = 3000;

const IN_PROGRESS_STATUSES = new Set(['RUNNING', 'QUEUED']);

function isInProgress(status?: string) {
  return !!status && IN_PROGRESS_STATUSES.has(status);
}

export const PLATFORM_MIGRATION_QUERY_KEY = {
  RUNS: 'PLATFORM_MIGRATION_RUNS',
  STATUS: 'PLATFORM_MIGRATION_STATUS',
  ITEMS: 'PLATFORM_MIGRATION_ITEMS',
  HEALTH_CHECK_STATUS: 'PLATFORM_MIGRATION_HEALTH_CHECK_STATUS',
  HEALTH_CHECK_ITEMS: 'PLATFORM_MIGRATION_HEALTH_CHECK_ITEMS',
  SYNC_STATUS: 'PLATFORM_MIGRATION_SYNC_STATUS',
  SYNC_ITEMS: 'PLATFORM_MIGRATION_SYNC_ITEMS',
  TENANT_OPTIONS: 'PLATFORM_MIGRATION_TENANT_OPTIONS',
  SYSTEM_OPTIONS: 'PLATFORM_MIGRATION_SYSTEM_OPTIONS',
};

// ── Migration Run hooks ──

export function usePlatformMigrationRuns() {
  return useQuery<AxiosResponse<PlatformMigrationRun[]>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.RUNS],
    () => axios.get('/api/v1/master/platform-migration/runs').then((res) => res),
    {
      refetchInterval: (data) => {
        const hasInProgress = data?.data?.some((run) => isInProgress(run.status));
        return hasInProgress ? POLL_INTERVAL : false;
      },
    },
  );
}

export function usePlatformMigrationStatus(runId?: string) {
  return useQuery<AxiosResponse<PlatformMigrationRun>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.STATUS, runId],
    () => axios.get(`/api/v1/master/platform-migration/status/${runId}`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: (data) => (isInProgress(data?.data?.status) ? POLL_INTERVAL : false),
    },
  );
}

export function usePlatformMigrationItems(runId?: string, isRunInProgress?: boolean) {
  return useQuery<AxiosResponse<PlatformMigrationRunItem[]>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.ITEMS, runId],
    () => axios.get(`/api/v1/master/platform-migration/status/${runId}/items`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: isRunInProgress ? POLL_INTERVAL : false,
    },
  );
}

export function useTriggerPlatformMigration() {
  const queryClient = useQueryClient();
  return useMutation<AxiosResponse<PlatformMigrationRunResponse>, AxiosDefaultErrorEntity, PlatformMigrationRunRequest>(
    (bodyRequest) => axios.post('/api/v1/master/platform-migration/run', bodyRequest),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(PLATFORM_MIGRATION_QUERY_KEY.RUNS);
      },
    },
  );
}

// ── Scope option pickers (tenant/system) for the trigger dialog ──
// Gated by `enabled` so the lists are only fetched when the relevant database
// group is selected and the dialog is open.

export function usePlatformMigrationTenantOptions(enabled?: boolean) {
  return useQuery<AxiosResponse<TenantListResponse>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.TENANT_OPTIONS],
    () =>
      axios
        .get(`/api/v1/master/tenant/list?${qs.stringify({ page: 0, size: SCOPE_OPTION_PAGE_SIZE })}`)
        .then((res) => res),
    { enabled },
  );
}

export function usePlatformMigrationSystemOptions(enabled?: boolean) {
  return useQuery<AxiosResponse<SystemListResponse>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.SYSTEM_OPTIONS],
    () =>
      axios
        .get(`/api/v1/master/system/list?${qs.stringify({ page: 0, size: SCOPE_OPTION_PAGE_SIZE })}`)
        .then((res) => res),
    { enabled },
  );
}

// ── Health Check hooks ──

export function useTriggerHealthCheck() {
  return useMutation<AxiosResponse<SyncRunResponse>, AxiosDefaultErrorEntity, HealthCheckRunRequest>(
    (bodyRequest) => axios.post('/api/v1/master/platform-migration/health-check/run', bodyRequest),
  );
}

export function useHealthCheckStatus(runId?: string) {
  return useQuery<AxiosResponse<HealthCheckRun>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.HEALTH_CHECK_STATUS, runId],
    () => axios.get(`/api/v1/master/platform-migration/health-check/status/${runId}`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: (data) => (isInProgress(data?.data?.status) ? POLL_INTERVAL : false),
    },
  );
}

export function useHealthCheckItems(runId?: string, isRunInProgress?: boolean) {
  return useQuery<AxiosResponse<HealthCheckItem[]>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.HEALTH_CHECK_ITEMS, runId],
    () => axios.get(`/api/v1/master/platform-migration/health-check/status/${runId}/items`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: isRunInProgress ? POLL_INTERVAL : false,
    },
  );
}

// ── Sync hooks ──

export function useTriggerFleetSync() {
  return useMutation<AxiosResponse<SyncRunResponse>, AxiosDefaultErrorEntity, SyncRunRequest>(
    (bodyRequest) => axios.post('/api/v1/master/platform-migration/sync/run', bodyRequest),
  );
}

export function useTriggerTenantSync() {
  return useMutation<AxiosResponse<SyncRunResponse>, AxiosDefaultErrorEntity, SyncRunRequest & { tenantId: string }>(
    ({ tenantId, ...bodyRequest }) => axios.post(`/api/v1/master/platform-migration/sync/tenant/${tenantId}`, bodyRequest),
  );
}

export function useTriggerSystemSync() {
  return useMutation<AxiosResponse<SyncRunResponse>, AxiosDefaultErrorEntity, SyncRunRequest & { systemId: string }>(
    ({ systemId, ...bodyRequest }) => axios.post(`/api/v1/master/platform-migration/sync/system/${systemId}`, bodyRequest),
  );
}

export function useSyncStatus(runId?: string) {
  return useQuery<AxiosResponse<SyncRun>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.SYNC_STATUS, runId],
    () => axios.get(`/api/v1/master/platform-migration/sync/status/${runId}`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: (data) => (isInProgress(data?.data?.status) ? POLL_INTERVAL : false),
    },
  );
}

export function useSyncItems(runId?: string, isRunInProgress?: boolean) {
  return useQuery<AxiosResponse<SyncItem[]>, AxiosDefaultErrorEntity>(
    [PLATFORM_MIGRATION_QUERY_KEY.SYNC_ITEMS, runId],
    () => axios.get(`/api/v1/master/platform-migration/sync/status/${runId}/items`).then((res) => res),
    {
      enabled: !!runId,
      refetchInterval: isRunInProgress ? POLL_INTERVAL : false,
    },
  );
}
